import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageHero, Reveal, Overline, CTAButton, ArrowLink, Stagger, StaggerItem } from "@/components/primitives";
import Seo from "@/components/Seo";
import { TOPICS } from "@/data/topics";
import { organizationLd, websiteLd, breadcrumbLd, faqLd, articleLd } from "@/lib/seo";
import { SponsorProposalBand } from "@/components/sections";

export default function TopicPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const t = TOPICS[slug];
  if (!t) return <Navigate to="/insights" replace />;

  const articleStub = { title: t.h1, dek: t.metaDescription, image: "https://bharatethanolforum.com/assets/site/og-image.jpg" };
  const jsonLd = [
    organizationLd(),
    websiteLd(),
    articleLd(articleStub, slug),
    breadcrumbLd(`/${slug}`),
    faqLd(t.faqs),
  ];

  return (
    <>
      <Seo title={`${t.seoTitle} | Bharat Ethanol Forum`} description={t.metaDescription} path={`/${slug}`} keywords={t.primaryKw} jsonLd={jsonLd} />

      <PageHero overline={t.kicker} title={t.h1} subtitle={t.definition} />

      {/* AI summary / definition block (GEO) */}
      <section className="bg-parchment py-14 md:py-20">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <Reveal>
            <div className="rounded-2xl border-l-4 border-emerald2 bg-white p-6 shadow-sm" data-testid="topic-definition">
              <Overline className="mb-3">In short</Overline>
              <p className="text-lg leading-relaxed text-navy/80">{t.definition}</p>
            </div>
          </Reveal>

          {/* Stats */}
          <Stagger className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {t.stats.map((s) => (
              <StaggerItem key={s.l}>
                <div className="h-full rounded-2xl border border-border bg-white p-6 text-center shadow-sm">
                  <div className="font-display text-3xl font-semibold tracking-tight text-emeraldDeep">{s.v}</div>
                  <p className="mt-2 text-xs leading-relaxed text-navy/60">{s.l}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Insight sections */}
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="space-y-10">
            {t.sections.map((s) => (
              <Reveal key={s.h}>
                <h2 className="font-display text-2xl font-bold tracking-tight text-navy">{s.h}</h2>
                <p className="mt-4 text-lg leading-relaxed text-navy/70">{s.p}</p>
              </Reveal>
            ))}
          </div>

          {/* Conversion CTAs */}
          <Reveal>
            <div className="mt-12 rounded-2xl border border-emerald2/30 bg-parchment p-7 text-center">
              <h3 className="font-display text-xl font-semibold text-navy">Go deeper at the Bharat Ethanol Forum 2026.</h3>
              <p className="mx-auto mt-2 max-w-xl text-navy/65">India's ethanol economy & biofuel leadership forum + expo — 28–29 September 2026, Mumbai.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <CTAButton to="/register" data-testid="topic-cta-register">Register</CTAButton>
                <CTAButton to="/sponsor" variant="outline" className="text-navy" data-testid="topic-cta-sponsor">Sponsor</CTAButton>
                <CTAButton to="/exhibit" variant="outline" className="text-navy" data-testid="topic-cta-exhibit">Exhibit</CTAButton>
              </div>
            </div>
          </Reveal>

          {/* FAQ (GEO + FAQPage schema) */}
          <div className="mt-14">
            <Overline className="mb-5">Frequently asked</Overline>
            <Accordion type="single" collapsible className="rounded-2xl border border-border bg-white px-2">
              {t.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-border" data-testid={`topic-faq-${i}`}>
                  <AccordionTrigger className="px-4 text-left font-display font-semibold text-navy hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="px-4 text-navy/70">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related topics (internal linking) */}
      <section className="border-t border-border bg-parchment py-14">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Overline className="mb-6">Related intelligence</Overline>
          <div className="grid gap-6 md:grid-cols-3">
            {t.related.map((rs) => {
              const r = TOPICS[rs];
              if (!r) return null;
              return (
                <Link key={rs} to={`/${rs}`} className="group rounded-2xl border border-border bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`topic-related-${rs}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-emeraldDeep">{r.kicker}</p>
                  <h3 className="mt-2 font-display text-lg font-bold text-navy">{r.h1}</h3>
                  <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-emeraldDeep transition-all group-hover:gap-3">Read more →</span>
                </Link>
              );
            })}
          </div>
          <Reveal className="mt-8"><ArrowLink to="/insights">Explore the full Insights hub</ArrowLink></Reveal>
        </div>
      </section>

      <SponsorProposalBand title="Reach the companies reading this — sponsor the forum." />
    </>
  );
}
